import { apiRequest } from "./api";
import type { BusinessWithAdminFlag } from "../types/index";
import type { Loan, LoanStatus, LoanInstallment } from "./loanApi";
import type { Leave, LeaveStatus } from "./leaveApi";
import type { Task, TaskStatus } from "./taskApi";
import type { AttendanceRecord, AttendanceStatus } from "./attendanceApi";
import type { EmployeeProfile } from "../types/index";

function biz(businessId: string) {
  return `/biz/${businessId}`;
}

export const bizApi = {
  // GET /me/businesses
  myBusinesses: () =>
    apiRequest<{ items: BusinessWithAdminFlag[] }>("/me/businesses"),

  // Users
  listUsers: (businessId: string) =>
    apiRequest<{ items: EmployeeProfile[] }>(`${biz(businessId)}/users`),

  // Loans
  listLoans: (businessId: string, status?: LoanStatus) =>
    apiRequest<{ items: Loan[] }>(
      `${biz(businessId)}/loans`,
      { params: status ? { status } : undefined },
    ),
  approveLoan: (businessId: string, loanId: string, userId: string) =>
    apiRequest<{ success: boolean }>(`${biz(businessId)}/loans/${loanId}/approve`, {
      method: "POST",
      body: { userId },
    }),
  rejectLoan: (
    businessId: string,
    loanId: string,
    userId: string,
    reason: string,
  ) =>
    apiRequest<{ success: boolean }>(`${biz(businessId)}/loans/${loanId}/reject`, {
      method: "POST",
      body: { userId, reason },
    }),
  releaseLoan: (
    businessId: string,
    loanId: string,
    userId: string,
    releaseDate: string,
    disbursementAccount?: string,
  ) =>
    apiRequest<{ loan: Loan; installments: LoanInstallment[] }>(
      `${biz(businessId)}/loans/${loanId}/release`,
      { method: "POST", body: { userId, releaseDate, ...(disbursementAccount ? { disbursementAccount } : {}) } },
    ),

  // Leaves
  listLeaves: (businessId: string, status?: LeaveStatus) =>
    apiRequest<{ items: Leave[] }>(
      `${biz(businessId)}/leaves`,
      { params: status ? { status } : undefined },
    ),
  approveLeave: (businessId: string, leaveId: string, userId: string, note?: string) =>
    apiRequest<{ success: boolean }>(`${biz(businessId)}/leaves/${leaveId}/approve`, {
      method: "POST",
      body: { userId, note },
    }),
  rejectLeave: (businessId: string, leaveId: string, userId: string, note?: string) =>
    apiRequest<{ success: boolean }>(`${biz(businessId)}/leaves/${leaveId}/reject`, {
      method: "POST",
      body: { userId, note },
    }),

  // Tasks
  listTasks: (businessId: string, status?: TaskStatus) =>
    apiRequest<{ items: Task[] }>(
      `${biz(businessId)}/tasks`,
      { params: status ? { status } : undefined },
    ),
  createTask: (
    businessId: string,
    payload: {
      assignedTo: string;
      title: string;
      description: string;
      priority: string;
      dueDate?: string;
    },
  ) =>
    apiRequest<Task>(`${biz(businessId)}/tasks`, {
      method: "POST",
      body: payload,
    }),

  // Attendance
  listAttendance: (businessId: string, date?: string) =>
    apiRequest<{ items: AttendanceRecord[] }>(
      `${biz(businessId)}/attendance`,
      { params: date ? { date } : undefined },
    ),
  listAttendanceRange: (businessId: string, from: string, to: string) =>
    apiRequest<{ items: AttendanceRecord[] }>(
      `${biz(businessId)}/attendance`,
      { params: { from, to } },
    ),
  upsertAttendance: (
    businessId: string,
    payload: {
      userId: string;
      employeeName?: string;
      date: string;
      status: AttendanceStatus;
      timeIn?: string;
      timeOut?: string;
      notes?: string;
    },
  ) =>
    apiRequest<AttendanceRecord>(`${biz(businessId)}/attendance`, {
      method: "POST",
      body: payload,
    }),

  // Per-business roles
  listPages: (businessId: string) =>
    apiRequest<{ pages: readonly string[] }>(`${biz(businessId)}/roles/pages`),
  listRoles: (businessId: string) =>
    apiRequest<{ items: unknown[] }>(`${biz(businessId)}/roles`),
  createRole: (businessId: string, payload: { name: string; description?: string; pages: string[] }) =>
    apiRequest<unknown>(`${biz(businessId)}/roles`, { method: "POST", body: payload }),
  updateRole: (businessId: string, roleId: string, payload: { name?: string; description?: string; pages?: string[] }) =>
    apiRequest<unknown>(`${biz(businessId)}/roles/${roleId}`, { method: "PUT", body: payload }),
  deleteRole: (businessId: string, roleId: string) =>
    apiRequest<void>(`${biz(businessId)}/roles/${roleId}`, { method: "DELETE" }),
};
